import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { isPresent } from "@ember/utils";
import { on } from "@ember/modifier";
import GInput from "glesys-controlpanel/components/g-input";

export type SearchableArgs<TItem> = {
  items: readonly TItem[];
  placeholder: string;
  emptyStateText?: string;
} & (
  | { keys: readonly (keyof TItem & string)[]; match?: never }
  | { keys?: never; match: (item: TItem, lowercasedQuery: string) => boolean }
);

export interface SearchableSignature<TItem> {
  Args: SearchableArgs<TItem>;
  Blocks: {
    default: [filtered: readonly TItem[]];
  };
  Element: HTMLDivElement;
}

export default class Searchable<TItem> extends Component<SearchableSignature<TItem>> {
  @tracked query = "";

  get filtered(): readonly TItem[] {
    if (!isPresent(this.query)) {
      return this.args.items;
    }

    const lowerCasedQuery = this.query.toLowerCase();

    if (this.args.match) {
      return this.args.items.filter((item) => this.args.match!(item, lowerCasedQuery));
    }

    return this.args.items.filter((item) =>
      this.args.keys?.some((key) =>
        String(item[key] ?? "")
          .toLowerCase()
          .includes(lowerCasedQuery),
      ),
    );
  }

  get showEmptyState(): boolean {
    return Boolean(this.args.emptyStateText) && this.filtered.length === 0;
  }

  onInput = (event: Event) => {
    this.query = (event.target as HTMLInputElement).value;
  };

  <template>
    <div ...attributes>
      <GInput
        aria-label={{@placeholder}}
        type="search"
        value={{this.query}}
        placeholder={{@placeholder}}
        {{on "input" this.onInput}}
      />

      {{#if this.showEmptyState}}
        <p class="py-10 text-center text-sm text-gray-500" data-test-searchable-empty>
          {{@emptyStateText}}
        </p>
      {{else}}
        {{yield this.filtered}}
      {{/if}}
    </div>
  </template>
}
